import { useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { useAuth } from '@/auth/AuthContext';
import { useLanguage } from '@/i18n/LanguageContext';
import { LanguageToggle } from '@/i18n/LanguageToggle';
import { pluralCategory } from '@/i18n/format';
import type { TranslationKey } from '@/i18n/dictionaries/en';

/**
 * COMMERCIAL LAYER epic (ADR-075 Part C). The seller portal's nav has no
 * permission-based hiding (apps/ops/src/components/Layout.tsx's NAV filter
 * exists because office roles differ; a seller portal session has no role
 * at all - every logged-in seller sees the same six sections, scoped to
 * their own owner(s) server-side).
 *
 * One outbound section: "Отгрузки" (/shipments) holds the seller's drafts, requests, orders and their shipment
 * result; the former separate "Orders" entry is gone (its URLs redirect, see App.tsx).
 */
const NAV: { to: string; labelKey: TranslationKey }[] = [
  { to: '/', labelKey: 'nav.dashboard' },
  { to: '/inventory', labelKey: 'nav.inventory' },
  { to: '/products', labelKey: 'nav.products' },
  { to: '/inbound', labelKey: 'nav.inbound' },
  { to: '/shipments', labelKey: 'nav.orders' },
  { to: '/returns', labelKey: 'nav.returns' },
  { to: '/billing', labelKey: 'nav.billing' },
  { to: '/integrations', labelKey: 'nav.integrations' },
];

const OWNERS_LINKED_KEY: Record<Intl.LDMLPluralRule, TranslationKey> = {
  zero: 'nav.ownersLinkedMany',
  one: 'nav.ownersLinkedOne',
  two: 'nav.ownersLinkedFew',
  few: 'nav.ownersLinkedFew',
  many: 'nav.ownersLinkedMany',
  other: 'nav.ownersLinkedOther',
};

export function AppLayout(): JSX.Element {
  const { state, logout } = useAuth();
  const { t, locale } = useLanguage();
  const self = state.status === 'authenticated' ? state.self : null;
  // The company name(s) this login works under: always visible, small. One -> plain label; several -> compact list.
  const companies = (self?.owners ?? []).map((o) => o.name);
  const companyText = companies.join(', ');
  const companyLabel = companies.length > 1 ? t('nav.companyMany') : t('nav.companyOne');
  // Off-canvas drawer below the app-shell's ~860px breakpoint (styles.css) -
  // above it, CSS overrides make .app-nav a normal static sidebar and this
  // state is simply unused (the topbar/backdrop stay display:none there).
  const [navOpen, setNavOpen] = useState(false);

  return (
    <div className="app-shell">
      <a className="skip-link" href="#main-content">
        {t('nav.skipToContent')}
      </a>
      <div className="app-topbar">
        <div className="brand-block">
          <div className="brand">{t('nav.brand')}</div>
          {companyText && <div className="company-label" data-testid="company-topbar" aria-label={companyLabel} title={companyText}>{companyText}</div>}
        </div>
        <button
          type="button"
          className="nav-toggle"
          aria-label={navOpen ? t('nav.closeMenu') : t('nav.openMenu')}
          aria-expanded={navOpen}
          onClick={() => setNavOpen((v) => !v)}
        >
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
            <path d="M2 4.5h14M2 9h14M2 13.5h14" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
          </svg>
        </button>
      </div>
      <div className={`nav-backdrop${navOpen ? ' open' : ''}`} onClick={() => setNavOpen(false)} />
      <nav className={`app-nav${navOpen ? ' open' : ''}`} aria-label="Primary">
        <div className="brand">{t('nav.brand')}</div>
        {companyText && <div className="company-label" data-testid="company-nav" aria-label={companyLabel} title={companyText}>{companyText}</div>}
        {NAV.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === '/'}
            className={({ isActive }) => (isActive ? 'active' : '')}
            onClick={() => setNavOpen(false)}
          >
            {t(item.labelKey)}
          </NavLink>
        ))}
        <LanguageToggle />
        {self && (
          <div className="who">
            <div>{t(OWNERS_LINKED_KEY[pluralCategory(self.ownerIds.length, locale)], { n: self.ownerIds.length })}</div>
            <button type="button" onClick={logout} style={{ marginTop: 8 }}>
              {t('nav.logOut')}
            </button>
          </div>
        )}
      </nav>
      <main className="app-main" id="main-content">
        <Outlet />
      </main>
    </div>
  );
}
